/**
 * Жанры и готовые задачи для первого экрана.
 *
 * Жанры лежат здесь плоскими строками, а не берутся из `DOCUMENT_KINDS`
 * намеренно. Витрина не импортирует контракты кабинета: за ними приезжает zod
 * и половина типов приложения, а человеку из поиска нужен лёгкий первый экран.
 * Идентификаторы обязаны совпадать с `DocumentKindSchema` — сервер проверит
 * жанр и откажет незнакомому, поэтому расхождение всплывёт на первом же
 * запросе, а не тихо.
 *
 * Показываются пять жанров из девяти: те, за которыми приходят. Остальные
 * («Кейс», «Отчёт», «Деловой документ», «Другое») есть в кабинете, и чипами на
 * первом экране их прятать не надо — надо не превращать первый экран в список
 * возможностей. «Приложение» здесь не потому, что за ним уже приходят, а
 * потому, что иначе о нём не узнать: человек с задачей «учёт заказов» видел
 * четыре документных чипа и уходил, не попробовав.
 */
export interface ShowcaseKind {
  id: string;
  /** Подпись на чипе. Короткая: чипы стоят в один ряд. */
  label: string;
  /** Что человек увидит в поле, пока не начал печатать. */
  placeholder: string;
}

export const SHOWCASE_KINDS: ShowcaseKind[] = [
  {
    id: 'offer',
    label: 'КП',
    placeholder: 'КП на монтаж вентиляции для «Северного ветра» — смета из прайса, сроки из созвона',
  },
  {
    id: 'landing',
    label: 'Лендинг',
    placeholder: 'Лендинг студии ремонта: услуги, цены, примеры работ и форма заявки',
  },
  {
    id: 'presentation',
    label: 'Презентация',
    placeholder: 'Презентация компании для тендера: опыт, команда, кейсы, что умеем',
  },
  {
    id: 'price',
    label: 'Прайс',
    placeholder: 'Прайс-лист оптовых цен по разделам, с условиями отгрузки и скидками',
  },
  {
    id: 'app',
    label: 'Приложение',
    placeholder:
      'Учёт заказов мастерской: клиент оформляет заказ на сайте, владелец видит список и меняет статус',
  },
];

/**
 * Готовые задачи.
 *
 * Не примеры «как надо писать», а рабочие заготовки: нажал — текст встал в
 * поле, поправил под себя, отправил. У приложения заготовка своя и нужна больше
 * остальных: как описать документ, человек знает, а что сказать про
 * приложение — сущности, роли, сценарий, статусы — ему неоткуда узнать. Так человек видит, ЧТО именно от него
 * ждут, не читая инструкции, — и заодно понимает глубину, на которую сервис
 * работает: не «сделай красиво», а задача с материалами, аудиторией и целью.
 *
 * Длина неслучайна. Короткая заготовка учила бы писать короткие задачи, а
 * короткая задача даёт документ из общих слов — и это худшее первое
 * впечатление, какое можно устроить.
 */
export interface Starter {
  id: string;
  /** Подпись на кнопке: задача одним словом. */
  label: string;
  kind: string;
  text: string;
}

export const STARTERS: Starter[] = [
  {
    id: 'offer-services',
    label: 'КП на услуги',
    kind: 'offer',
    text: [
      'Коммерческое предложение на обслуживание вентиляции для управляющей компании.',
      '',
      'Кто мы: инженерная компания, 8 лет на рынке, 40 объектов на обслуживании в городе.',
      'Кому: главный инженер УК, у него 12 жилых домов и предписание от надзора.',
      'Его боль: аварийные вызовы зимой, жалобы жильцов на запахи, штрафы за просрочку.',
      '',
      'Что предлагаем: регламентное обслуживание раз в квартал, аварийный выезд за 4 часа,',
      'ведение журналов и подготовка к проверкам. Три тарифа: базовый, с диспетчерской,',
      'с заменой узлов. Цены за дом в месяц, договор на год.',
      '',
      'Что должно быть в документе: разбор его ситуации, состав работ по пунктам, таблица',
      'тарифов, сроки реакции, два похожих объекта в работе, условия договора и следующий',
      'шаг — выезд на осмотр. Тон деловой, без рекламных обещаний.',
    ].join('\n'),
  },
  {
    id: 'landing-request',
    label: 'Лендинг с заявкой',
    kind: 'landing',
    text: [
      'Лендинг студии кухонь на заказ, трафик пойдёт с рекламы.',
      '',
      'Кто мы: небольшое производство, свой цех, делаем кухни по индивидуальным размерам,',
      'срок от замера до установки — 30 дней.',
      'Кому: семья, которая закончила ремонт и выбирает между сетевым салоном и частником.',
      'Сомнения: «сделают дольше обещанного», «на замере накрутят цену», «кто чинит потом».',
      '',
      'Что должно быть на странице: первый экран с понятным обещанием и кнопкой на замер,',
      'для кого мы и для кого нет, как считается цена — честно, от чего зависит, этапы',
      'работы по неделям, примеры готовых кухонь с ценой, гарантия и сервис, ответы на',
      'возражения выше, форма заявки с именем и телефоном.',
      '',
      'Действие на странице одно: записаться на бесплатный замер. Всё остальное ведёт к нему.',
    ].join('\n'),
  },
  {
    id: 'deck-company',
    label: 'Презентация компании',
    kind: 'presentation',
    text: [
      'Презентация компании для участия в тендере на подрядные работы.',
      '',
      'Кто мы: подрядчик по электромонтажу промышленных объектов, 15 лет, штат 60 человек,',
      'своя аттестованная лаборатория, допуски СРО.',
      'Кому показываем: тендерная комиссия завода — там технический директор, безопасник',
      'и закупщик. Смотрят 5 минут, сравнивают с четырьмя другими подрядчиками.',
      '',
      'Что они должны увидеть: что мы делали объекты такого же масштаба, что у нас есть',
      'люди и техника под их сроки, что мы не сорвём график и не нарушим требования',
      'безопасности, что с нами просто работать по документам.',
      '',
      'Состав: кто мы в двух цифрах, опыт на похожих объектах, три кейса с фактами —',
      'объект, срок, что сделали, команда и техника, допуски и аттестации, как ведём',
      'работы и отчётность, контакты. Без воды и без стоковых картинок.',
    ].join('\n'),
  },
  {
    id: 'app-orders',
    label: 'Учёт заказов',
    kind: 'app',
    text: [
      'Приложение для учёта заказов небольшой мебельной мастерской.',
      '',
      'Кто пользуется: клиент оформляет заказ на сайте — выбирает изделие из каталога,',
      'пишет размеры и телефон; владелец видит все заказы списком и ведёт их по этапам.',
      'Сейчас заказы записываются в тетрадь и теряются, клиенты звонят спросить «когда».',
      '',
      'Что учитываем: заказ (изделие, размеры, сумма в рублях, дата сдачи, телефон клиента,',
      'этап), каталог изделий с ценами — его правит владелец сам. Этапы заказа:',
      'новый → в работе → готов → выдан. Клиент по ссылке видит этап своего заказа.',
      '',
      'Главный сценарий: оформить заказ и получить подтверждение с номером. Владельцу —',
      'уведомление в Telegram о каждом новом заказе. Первая версия — без оплаты онлайн.',
    ].join('\n'),
  },
];
